import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../lib/utils';
import { Button } from './ui/button';
import { BadgeCheck, MapPin, Video, Users, ArrowRight, CalendarCheck } from 'lucide-react';

const DELIVERY_LABEL = { virtual: 'Virtual', in_person: 'In-person', both: 'Virtual & In-person' };

const initials = (name = '') =>
  name.replace(/,.*$/, '').split(' ').filter(Boolean).slice(0, 2).map((n) => n[0]).join('').toUpperCase() || 'SW';

const SupervisorCard = ({ s }) => {
  const cityState = [s.city, s.license_state].filter(Boolean).join(', ');
  const specs = (s.specialties || []).slice(0, 3);
  const delivery = DELIVERY_LABEL[s.delivery] || null;
  const track = () => {
    axios.post(`${API_URL}/supervisors/${s.user_id}/track-featured`, { event: 'click' }).catch(() => {});
  };
  return (
    <div className="bg-white rounded-2xl border border-primary/10 p-5 flex flex-col hover:shadow-lg transition-all" data-testid={`featured-supervisor-${s.user_id}`}>
      <div className="flex items-center gap-3">
        {s.photo_url ? (
          <img src={s.photo_url} alt={s.full_name} className="w-14 h-14 rounded-full object-cover ring-2 ring-secondary/30 flex-shrink-0" />
        ) : (
          <div className="w-14 h-14 rounded-full bg-primary/10 text-primary font-bold flex items-center justify-center text-lg flex-shrink-0">{initials(s.full_name)}</div>
        )}
        <div className="min-w-0">
          <h3 className="font-serif font-bold text-primary leading-tight truncate">{s.full_name}</h3>
          <div className="flex items-center gap-1 mt-0.5">
            {s.license_type && <span className="text-xs font-semibold text-gray-600">{s.license_type}</span>}
            <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-emerald-600" data-testid="verified-badge">
              <BadgeCheck className="w-3.5 h-3.5" /> Verified
            </span>
          </div>
        </div>
      </div>

      {cityState && (
        <p className="text-xs text-gray-500 mt-3 flex items-center gap-1"><MapPin className="w-3.5 h-3.5 text-secondary" />{cityState}</p>
      )}

      {specs.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {specs.map((sp) => (
            <span key={sp} className="text-[11px] bg-secondary/10 text-secondary font-medium px-2 py-0.5 rounded-full">{sp}</span>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 mt-3 text-[11px] text-gray-500">
        {delivery && <span className="flex items-center gap-1"><Video className="w-3.5 h-3.5 text-primary" />{delivery}</span>}
        {s.availability && <span className="flex items-center gap-1"><CalendarCheck className="w-3.5 h-3.5 text-primary" />{s.availability}</span>}
      </div>

      <Link to={`/supervisors/${s.user_id}`} onClick={track} className="mt-4" data-testid={`featured-supervisor-view-${s.user_id}`}>
        <Button className="w-full bg-primary hover:bg-primary/90 text-white h-9">View Profile <ArrowRight className="w-4 h-4 ml-1" /></Button>
      </Link>
    </div>
  );
};

const FeaturedSupervisors = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/marketplace/featured-supervisors?limit=10`)
      .then((r) => setData(r.data))
      .catch(() => setData({ supervisors: [] }));
  }, []);

  if (!data || !data.supervisors || data.supervisors.length === 0) return null;

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-10" data-testid="featured-supervisors-section">
      <div className="text-center mb-6">
        <h2 className="font-serif text-2xl md:text-3xl font-bold text-primary flex items-center justify-center gap-2">
          <Users className="w-6 h-6 text-secondary" /> Featured Verified Social Work Supervisors
        </h2>
        <p className="text-sm md:text-base text-gray-600 mt-2 max-w-2xl mx-auto">
          Connect with licensed social-work supervisors offering clinical supervision across the United States.
        </p>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {data.supervisors.map((s) => <SupervisorCard key={s.user_id} s={s} />)}
      </div>
      <div className="text-center mt-6">
        <Link to="/find-a-supervisor" data-testid="browse-all-supervisors">
          <Button variant="outline" className="border-2 border-primary text-primary hover:bg-primary hover:text-white h-11 px-8">
            Browse All Supervisors <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </Link>
      </div>
    </section>
  );
};

export default FeaturedSupervisors;
